/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

/** Non-overlapping channel pools by band (MHz channel numbers). */
export function channelPoolForBand(bandGHz: number): number[] {
  if (bandGHz >= 6) return [1, 5, 9, 13, 17, 21, 25, 29, 33, 37]
  if (bandGHz >= 5) return [36, 40, 44, 48, 52, 56, 60, 64, 149, 153, 157, 161]
  return [1, 6, 11]
}

/** Whether two channels contribute co-channel / adjacent-channel interference on this band. */
export function channelsInterfere(ch1: number | undefined, ch2: number | undefined, bandGHz: number): boolean {
  if (ch1 == null || ch2 == null) return false
  if (bandGHz >= 5) return ch1 === ch2
  return Math.abs(ch1 - ch2) < 5
}

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

/** Greedy channel assignment — minimize conflicts among APs within ~25 m. */
export function assignOptimalChannels(
  aps: { id: string; x: number; y: number; channel?: number }[],
  bandGHz: number,
  floorWidthM: number,
  floorHeightM: number,
): Record<string, number> {
  const pool = channelPoolForBand(bandGHz)
  if (aps.length === 0) return {}

  const conflictRadiusM = bandGHz >= 5 ? 22 : 28
  const byConflictCount = [...aps].sort((a, b) => {
    const na = aps.filter(o => o.id !== a.id && distanceM(a.x, a.y, o.x, o.y, floorWidthM, floorHeightM) < conflictRadiusM).length
    const nb = aps.filter(o => o.id !== b.id && distanceM(b.x, b.y, o.x, o.y, floorWidthM, floorHeightM) < conflictRadiusM).length
    return nb - na
  })

  const assigned: Record<string, number> = {}
  for (const ap of byConflictCount) {
    let bestCh = pool[0]
    let bestScore = Infinity
    for (const ch of pool) {
      let score = 0
      for (const other of aps) {
        if (other.id === ap.id) continue
        const d = distanceM(ap.x, ap.y, other.x, other.y, floorWidthM, floorHeightM)
        if (d >= conflictRadiusM) continue
        const otherCh = assigned[other.id]
        if (otherCh != null && channelsInterfere(ch, otherCh, bandGHz)) {
          score += conflictRadiusM - d
        }
      }
      if (score < bestScore) {
        bestScore = score
        bestCh = ch
      }
    }
    assigned[ap.id] = bestCh
  }
  return assigned
}

export function formatChannelLabel(channel: number | undefined, bandGHz: number): string {
  if (channel == null) return 'Auto'
  return bandGHz >= 5 ? `Ch ${channel}` : `Ch ${channel} (${bandGHz} GHz)`
}
